import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/services", label: "Services", testid: "nav-services" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

const Logo = ({ className = "" }) => (
  <Link to="/" data-testid="nav-logo" className={`flex items-center gap-3 group ${className}`}>
    <span className="inline-block w-6 h-6 border border-amber-500 relative">
      <span className="absolute inset-1 bg-amber-500/80 group-hover:bg-amber-400 transition-colors" />
    </span>
    <span className="font-serif text-2xl tracking-tight text-slate-50">
      Chesterton<span className="text-amber-500">.</span>
    </span>
  </Link>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 border-b ${
        scrolled
          ? "bg-slate-950/85 backdrop-blur-xl border-slate-800"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Logo />

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `text-xs uppercase tracking-[0.22em] font-medium transition-colors link-underline ${
                  isActive ? "text-amber-500" : "text-slate-300 hover:text-slate-50"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex">
          <Link
            to="/contact"
            data-testid="nav-cta-consult"
            className="group inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white px-5 py-3 text-xs uppercase tracking-[0.22em] font-medium transition-colors"
          >
            Request Consultation
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          className="md:hidden text-slate-50 p-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden transition-[max-height] duration-500 bg-slate-950/95 backdrop-blur-xl border-t border-slate-800 ${
          open ? "max-h-[400px]" : "max-h-0"
        }`}
      >
        <div className="px-6 py-8 flex flex-col gap-6">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`${item.testid}-mobile`}
              className={({ isActive }) =>
                `font-serif text-3xl ${isActive ? "text-amber-500" : "text-slate-50"}`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-consult-mobile"
            className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white px-5 py-3 text-xs uppercase tracking-[0.22em] font-medium w-fit"
          >
            Request Consultation
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </header>
  );
};

const Footer = () => {
  return (
    <footer data-testid="site-footer" className="bg-slate-950 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20">
          <div className="md:col-span-5">
            <p className="overline mb-6">Chesterton Security</p>
            <h3 className="font-serif text-3xl sm:text-4xl text-slate-50 leading-[1.05] mb-6">
              Disciplined counsel for industries where failure is not an option.
            </h3>
            <p className="text-slate-400 text-sm leading-relaxed max-w-md">
              We help industrial operators, exporters, and critical-infrastructure
              owners design, implement, and audit safety, security, and trade-compliance
              programs that withstand scrutiny.
            </p>
          </div>

          <div className="md:col-span-3 md:col-start-7">
            <p className="overline mb-5">Navigate</p>
            <ul className="space-y-3">
              {NAV.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    data-testid={`footer-${item.testid}`}
                    className="text-slate-300 hover:text-amber-500 transition-colors link-underline text-sm"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="overline mb-5">Contact</p>
            <address className="not-italic text-sm text-slate-300 leading-relaxed">
              <span className="block text-slate-400">629-247-6746</span>
              <a
                href="mailto:info@chestertonsecurity.com"
                data-testid="footer-email"
                className="block text-amber-500 hover:text-amber-400 link-underline mt-1"
              >
                info@chestertonsecurity.com
              </a>
            </address>
          </div>
        </div>

        {/* Massive serif wordmark */}
        <div className="border-t border-slate-800 pt-12">
          <div className="font-serif text-[18vw] md:text-[14vw] leading-[0.85] text-slate-50/[0.04] tracking-tighter select-none">
            Chesterton.
          </div>
        </div>

        <div className="mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} Chesterton LLC. All rights reserved.</p>
          <p className="tracking-[0.22em] uppercase">Discreet · Senior · Accountable</p>
        </div>
      </div>
    </footer>
  );
};

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-slate-950 text-slate-100">
      <Header />
      <main className="flex-1 pt-20">{children}</main>
      <Footer />
    </div>
  );
}
